<template>
  <div class="upload-container">
    <p class="upload-container__title">Upload QHA Data</p>
    <p class="upload-container__subtitle">
      QHA details should be uploaded using the excel template link
    </p>
    <div
      class="upload-container__dropbox"
      @mouseover="dragEnabled = true"
      @mouseleave="dragEnabled = false"
      @drop="handleExcelUpload"
      :class="{ 'is-active': dragEnabled }"
      @click="$refs['excel-import'].click()"
    >
      <input
        type="file"
        accept=".xls, .xlsx"
        ref="excel-import"
        hidden
        @change="handleExcelUpload"
      />
      <p>
        Click here, or drag and drop your CSV or Excel spreadsheet in this box.
      </p>
      <!-- <button class="button">Click here to upload your Master Excel template</button> -->
    </div>
    <a :href="`${publicPath}QHATemplate5.2.xlsx`" download="QHATemplate5.2.xlsx" class="download-template">Download QHA Excel Spreadsheet Template
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" class="icon icon-tabler icon-tabler-file-download"><path stroke="none" d="M0 0h24v24H0z"/><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2zM12 17v-6"/><path d="M9.5 14.5 12 17l2.5-2.5"/></svg>
    </a>
    <div class="button-row">
      <button v-if="uploadedPlans.length" class="button" @click="save">
        Save
      </button>
    </div>
    <div>
      <div class="table-wrapper">
        <table class="table">
          <thead>
            <tr>
              <th>*C/YP ID</th>
              <th>Service</th>
              <th>Health Professional</th>
              <th>Key Stage</th>
              <th>Primary Need</th>
              <th>Setting Name</th>
              <th>Report Date</th>
              <th>Plan 12mths +?</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(plan, i) in uploadedPlans" :key="i">
              <td>{{ plan.internalId }}</td>
              <td>{{ plan.service }}</td>
              <td>{{ plan.healthProfessional }}</td>
              <td>{{ plan.stage }}</td>
              <td>{{ plan.need }}</td>
              <td>{{ plan.settingName }}</td>
              <td>{{ plan.reportDate | formatDate }}</td>
              <td>
                <input
                  type="checkbox"
                  v-model="plan.historical"
                  :id="plan.internalId"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { format } from 'date-fns'
import XLSX from 'xlsx'
import { displaySuccess, displayWarning } from '@/utils/notification-factory'
import { message } from '@/utils/app-messages'
export default {
  name: 'UploadQHA',
  data() {
    return {
      publicPath: process.env.BASE_URL,
      dragEnabled: false,
      template: {
        Service: 'service',
        'Internal ID': 'internalId',
        'Health Professional': 'healthProfessional',
        'Key Stage': 'stage',
        'Primary Need': 'need',
        'Setting Name': 'settingName',
        'Report Date': 'reportDate'
      },
      tableData: []
    }
  },
  computed: {
    ...mapGetters({ plans: 'plan/plans' }),
    uploadedPlans() {
      return this.tableData
    }
  },
  filters: {
    formatDate(val) {
      return format(val, 'PPP')
    }
  },
  methods: {
    ...mapActions({ uploadPlans: 'plan/uploadPlans' }),
    handleExcelUpload(event) {
      this.tableData = []
      const spreadsheet = event.target.files[0]
      const reader = new FileReader()
      reader.onload = target => {
        const data = target.target.result
        const workbook = XLSX.read(data, { type: 'binary', cellDates: true })
        this.processSheet(workbook)
      }
      reader.readAsBinaryString(spreadsheet)
    },
    processSheet(workbook) {
      const workingSheetName = workbook.SheetNames[0]
      const workingSheet = workbook.Sheets[workingSheetName]
      const collectionTest = XLSX.utils.sheet_to_json(workingSheet) //, { header: 1, raw: false })
      const mapped = []
      collectionTest.map(collection => {
        let mappedRow = {}
        Object.keys(collection).forEach(key => {
          const value = collection[key]
          const objKey = this.template[key]
          mappedRow = { ...mappedRow, [objKey]: value }
        })
        mappedRow.historical = false
        mapped.push(mappedRow)
        return mappedRow
      })

      const invalidEntries = mapped
        .map((v, i) => ({ ...v, row: i + 2 }))
        .filter(
          ({
            internalId,
            service,
            stage,
            need,
            reportDate,
            historical
          }) =>
            !internalId ||
            !service ||
            !stage ||
            !need ||
            !reportDate ||
            historical === undefined
        )
      if (invalidEntries.length) {
        displayWarning(
          message.PLEASE_COMPLETE_MANDATORY_FIELDS(invalidEntries),
          true
        )
        return
      }
      this.tableData = mapped
    },
    save() {
      if (!this.tableData.length) {
        return displayWarning(message.PLEASE_UPLOAD_PLANS)
      }
      this.uploadPlans(this.tableData)
        .then(() => {
          displaySuccess(message.SUCCESSFULLY_UPLOADED_PLANS)
          this.tableData = []
        })
        .catch(err => displayWarning(message.FAILED_TO_UPLOAD_PLANS(err)))
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';

.download-template {
  display: inline;
  color: $brand-primary;

  svg {
    display: inline-block;
  }

  &:hover {
    color:  $brand-secondary;

    svg {
      display: inline;
    }
  }
}

.upload-container {
  width: 100%;
  position: relative;
  display: grid;
  grid-template-rows: 46px repeat(3, auto);
  grid-auto-flow: unset;
  &__title {
    margin: 0;
    font-weight: 600;
    font-size: 1.25rem;
  }
  &__subtitle {
    font-size: 1rem;
    font-weight: 400;
    &--bold {
      font-weight: 600;
    }
  }
  &__dropbox {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    border: 1px dashed lightgrey;
    border-radius: $border-radius;
    padding: 1rem;
    margin: 1rem 0;
    font-size: 1.25rem;
  }
  .is-active {
    background: #42ab983b;
  }
  .button-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>
